import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Truck, MapPin, Navigation, Phone, MessageSquare, Camera, Clock,
  Package, ChevronRight, AlertTriangle, CheckCircle, User, Fuel,
  Gauge, ThermometerSun, RefreshCw, ArrowLeft, ExternalLink,
  MoreVertical, FileText, Image as ImageIcon } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';

import { useFleetAPI } from '../hooks/useFleetData';

// Status configurations
const tripStatusConfig = {
  planned: { color: 'bg-gray-500', label: 'Planned' },
  assigned: { color: 'bg-purple-500', label: 'Assigned' },
  in_progress: { color: 'bg-blue-500', label: 'In Progress' },
  completed: { color: 'bg-emerald-500', label: 'Completed' },
  cancelled: { color: 'bg-red-500', label: 'Cancelled' },
  delayed: { color: 'bg-amber-500', label: 'Delayed' }
};

const stopStatusConfig = {
  pending: { color: 'bg-gray-400', icon: Clock },
  arrived: { color: 'bg-blue-500', icon: MapPin },
  completed: { color: 'bg-emerald-500', icon: CheckCircle },
  skipped: { color: 'bg-amber-500', icon: AlertTriangle },
  failed: { color: 'bg-red-500', icon: AlertTriangle }
};

const TripDetailPage = () => {const { t } = useTranslation("fleet");
  const { tripId } = useParams();
  const [trip, setTrip] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [trackingHistory, setTrackingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    const fetchTripData = async () => {
      setLoading(true);
      try {
        // Fetch trip details
        const tripData = await fetchAPI(`/trips/${tripId}`);
        if (tripData) {
          setTrip(tripData);

          // Fetch vehicle details if available
          if (tripData.vehicle?.id) {
            const vehicleData = await fetchAPI(`/vehicles/${tripData.vehicle.id}`);
            if (vehicleData) {
              setVehicle(vehicleData);
            }
          }

          // Fetch tracking history
          if (tripData.vehicle?.id) {
            const historyData = await fetchAPI(`/tracking/history/${tripData.vehicle.id}?limit=100`);
            if (historyData) {
              setTrackingHistory(historyData);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch trip data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (tripId) {
      fetchTripData();
    }
  }, [tripId, fetchAPI]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>);

  }

  if (!trip) {
    return (
      <div className="p-6">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("tripNotFound", "Trip Not Found")}</h3>
            <p className="text-gray-500 mb-4">{t("theRequestedTrip", "The requested trip could not be found.")}</p>
            <Link to="/ezparcel/fleet">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />{t("backToFleet", "Back to Fleet")}

              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>);

  }

  const tripStatus = tripStatusConfig[trip.status] || { color: 'bg-gray-500', label: trip.status };
  const routeProgress = trip.stops?.length > 0 ?
  trip.stops.filter((s) => s.status === 'completed').length / trip.stops.length * 100 :
  0;

  // Calculate capacity used (mock for now)
  const capacityUsed = trip.cargo?.weight_kg && vehicle?.max_load_kg ?
  Math.min(trip.cargo.weight_kg / vehicle.max_load_kg * 100, 100) :
  75;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/ezparcel/fleet">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />{t("back", "Back")}

            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{trip.trip_code}</h1>
              <Badge className={`${tripStatus.color} text-white`}>
                {tripStatus.label}
              </Badge>
            </div>
            <p className="text-gray-500">
              {trip.origin?.name || trip.origin?.address} → {trip.destination?.name || trip.destination?.address}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <MessageSquare className="w-4 h-4 mr-2" />{t("chatWithDriver", "Chat with Driver")}

          </Button>
          <Button className="bg-red-500 hover:bg-red-600 text-white" size="sm">
            <Navigation className="w-4 h-4 mr-2" />{t("liveTrack", "Live Track")}

          </Button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Map & Route */}
        <div className="lg:col-span-2 space-y-6">
          {/* Map Card */}
          <Card className="bg-white border-0 shadow-sm overflow-hidden">
            <CardHeader className="border-b bg-gray-50 py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">{t("liveTracking", "Live Tracking")}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>{t("live", "Live")}

                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Map placeholder - integrate with Leaflet/Google Maps */}
              <div className="h-80 bg-gradient-to-br from-blue-50 to-blue-100 relative flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                  <p className="text-blue-600 font-medium">{t("interactiveMapView", "Interactive Map View")}</p>
                  <p className="text-blue-400 text-sm">{t("routeVisualizationWith", "Route visualization with live vehicle position")}</p>
                </div>

                {/* Route line visualization placeholder */}
                <div className="absolute inset-4 pointer-events-none">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path
                      d="M10,80 Q30,20 50,50 T90,20"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="0.5"
                      strokeDasharray="2,1" />
                    
                    {/* Origin marker */}
                    <circle cx="10" cy="80" r="2" fill="#22c55e" />
                    {/* Destination marker */}
                    <circle cx="90" cy="20" r="2" fill="#ef4444" />
                    {/* Vehicle position */}
                    <circle cx="50" cy="50" r="3" fill="#3b82f6" className="animate-pulse" />
                  </svg>
                </div>
              </div>

              {/* Route Info Bar */}
              <div className="p-4 bg-white border-t grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {trip.metrics?.actual_distance_km || trip.metrics?.planned_distance_km || '0'}{t("km", "km")}
                  </p>
                  <p className="text-xs text-gray-500">{t("totalDistance", "Total Distance")}</p>
                </div>
                <div className="text-center border-x">
                  <p className="text-2xl font-bold text-blue-600">
                    {trip.schedule?.actual_start ?
                    `${Math.round((new Date() - new Date(trip.schedule.actual_start)) / 60000)} min` :
                    trip.metrics?.planned_duration_minutes ?
                    `${trip.metrics.planned_duration_minutes} min` :
                    '--'
                    }
                  </p>
                  <p className="text-xs text-gray-500">{t("duration", "Duration")}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">
                    {trip.efficiency_score ? `${Math.round(trip.efficiency_score)}%` : '96%'}
                  </p>
                  <p className="text-xs text-gray-500">{t("routeEfficiency", "Route Efficiency")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stops Timeline */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">{t("routeStops", "Route Stops")}</CardTitle>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="font-medium text-gray-900">{trip.stops?.filter((s) => s.status === 'completed').length || 0}</span>
                  <span>/</span>
                  <span>{trip.stops?.length || 0}{t("completed", "completed")}</span>
                </div>
              </div>
              <Progress value={routeProgress} className="h-1.5 mt-2" />
            </CardHeader>
            <CardContent className="p-0 max-h-96 overflow-auto">
              <div className="divide-y">
                {/* Origin */}
                <div className="p-4 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900">{t("origin", "Origin")}</p>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{t("started", "Started")}

                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{trip.origin?.address}</p>
                    {trip.schedule?.actual_start &&
                    <p className="text-xs text-gray-400 mt-1">{t("departed", "Departed:")}
                      {new Date(trip.schedule.actual_start).toLocaleTimeString()}
                      </p>
                    }
                  </div>
                </div>

                {/* Stops */}
                {(trip.stops || []).map((stop, idx) => {
                  const stopStatus = stopStatusConfig[stop.status] || stopStatusConfig.pending;
                  const StopIcon = stopStatus.icon;

                  return (
                    <div key={stop.id || idx} className="p-4 flex items-start gap-4 hover:bg-gray-50">
                      <div className={`w-10 h-10 rounded-full ${stopStatus.color} bg-opacity-20 flex items-center justify-center flex-shrink-0`}>
                        <StopIcon className={`w-5 h-5 ${stopStatus.color.replace('bg-', 'text-')}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900">{t("stop", "Stop")}
                            {stop.sequence || idx + 1}: {stop.name || 'Delivery'}
                          </p>
                          <Badge variant="outline" className={`text-xs ${stop.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          stop.status === 'arrived' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-gray-50 text-gray-600 border-gray-200'}`
                          }>
                            {stop.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{stop.address}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          {stop.scheduled_arrival &&
                          <span>{t("eta", "ETA:")}{new Date(stop.scheduled_arrival).toLocaleTimeString()}</span>
                          }
                          {stop.actual_arrival &&
                          <span className="text-emerald-600">{t("arrived", "Arrived:")}
                            {new Date(stop.actual_arrival).toLocaleTimeString()}
                            </span>
                          }
                          {stop.shipment_id &&
                          <span className="flex items-center">
                              <Package className="w-3 h-3 mr-1" />
                              {stop.shipment_id}
                            </span>
                          }
                        </div>
                      </div>
                    </div>);

                })}

                {/* Destination */}
                <div className="p-4 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900">{t("destination", "Destination")}</p>
                      <Badge variant="outline" className={`text-xs ${trip.status === 'completed' ?
                      'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      'bg-gray-50 text-gray-600 border-gray-200'}`
                      }>
                        {trip.status === 'completed' ? 'Arrived' : 'Pending'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{trip.destination?.address}</p>
                    {trip.schedule?.scheduled_end &&
                    <p className="text-xs text-gray-400 mt-1">{t("expected", "Expected:")}
                      {new Date(trip.schedule.scheduled_end).toLocaleTimeString()}
                      </p>
                    }
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Vehicle & Details */}
        <div className="space-y-6">
          {/* Vehicle Info Card */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-3">
              <CardTitle className="text-base font-semibold">{t("vehicleInfo", "Vehicle Info")}</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {trip.vehicle ?
              <>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center text-3xl">{t("key_7908", "\uD83D\uDE9B")}

                  </div>
                    <div>
                      <p className="font-bold text-gray-900">{trip.vehicle.code}</p>
                      <p className="text-sm text-gray-500">{trip.vehicle.license_plate}</p>
                      <p className="text-sm text-gray-500">{trip.vehicle.make} {trip.vehicle.model}</p>
                    </div>
                  </div>

                  {/* Truck Capacity */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">{t("truckCapacity", "Truck Capacity")}</span>
                      <span className="font-bold text-gray-900">{Math.round(capacityUsed)}%</span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                      className={`h-full rounded-full transition-all ${capacityUsed > 90 ? 'bg-red-500' :
                      capacityUsed > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`
                      }
                      style={{ width: `${capacityUsed}%` }} />
                    
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                      <span>{trip.cargo?.weight_kg || 0}{t("kgLoaded", "kg loaded")}</span>
                      <span>{t("max", "Max:")}{vehicle?.max_load_kg || 1500}{t("kg", "kg")}</span>
                    </div>
                  </div>

                  {/* Vehicle Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <Gauge className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                      <p className="text-lg font-bold text-gray-900">
                        {vehicle?.odometer_km ? Math.round(vehicle.odometer_km).toLocaleString() : '--'}
                      </p>
                      <p className="text-xs text-gray-500">{t("odometerKm", "Odometer (km)")}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <Fuel className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                      <p className="text-lg font-bold text-gray-900">
                        {vehicle?.current_fuel_level ? `${Math.round(vehicle.current_fuel_level)}%` : '--'}
                      </p>
                      <p className="text-xs text-gray-500">{t("fuelLevel", "Fuel Level")}</p>
                    </div>
                  </div>
                </> :

              <div className="text-center py-6">
                  <Truck className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">{t("noVehicleAssigned", "No vehicle assigned")}</p>
                </div>
              }
            </CardContent>
          </Card>

          {/* Driver Info Card */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-3">
              <CardTitle className="text-base font-semibold">{t("driver", "Driver")}</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {trip.driver ?
              <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{trip.driver.name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {[1, 2, 3, 4, 5].map((i) =>
                      <span key={i} className="text-amber-400">★</span>
                      )}
                        <span className="text-sm text-gray-500 ml-1">{t("50", "5.0")}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" size="sm">
                      <Phone className="w-4 h-4 mr-2" />{t("call", "Call")}

                  </Button>
                    <Button variant="outline" className="flex-1" size="sm">
                      <MessageSquare className="w-4 h-4 mr-2" />{t("message", "Message")}

                  </Button>
                  </div>
                </div> :

              <div className="text-center py-6">
                  <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">{t("noDriverAssigned", "No driver assigned")}</p>
                </div>
              }
            </CardContent>
          </Card>

          {/* Cargo Info Card */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">{t("cargoDetails", "Cargo Details")}</CardTitle>
                <Badge variant="outline" className="text-xs">
                  {trip.shipment_ids?.length || 0}{t("shipments", "shipments")}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">{t("weight", "Weight")}</p>
                  <p className="font-medium">{trip.cargo?.weight_kg || 0}{t("kg", "kg")}</p>
                </div>
                <div>
                  <p className="text-gray-500">{t("volume", "Volume")}</p>
                  <p className="font-medium">{trip.cargo?.volume_m3 || 0}{t("m", "m\xB3")}</p>
                </div>
                <div>
                  <p className="text-gray-500">{t("value", "Value")}</p>
                  <p className="font-medium">
                    {trip.cargo?.value ? `R$ ${trip.cargo.value.toLocaleString()}` : '--'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">{t("items", "Items")}</p>
                  <p className="font-medium">{trip.shipment_ids?.length || 0}</p>
                </div>
              </div>

              {trip.cargo?.description &&
              <div className="pt-3 border-t">
                  <p className="text-sm text-gray-500">{t("description", "Description")}</p>
                  <p className="text-sm font-medium mt-1">{trip.cargo.description}</p>
                </div>
              }
            </CardContent>
          </Card>

          {/* Cargo Photos */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="border-b py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">{t("cargoPhotos", "Cargo Photos")}</CardTitle>
                <Button variant="ghost" size="sm" className="text-red-500">
                  <Camera className="w-4 h-4 mr-1" />{t("add", "Add")}

                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {trip.cargo?.photos && trip.cargo.photos.length > 0 ?
              <div className="grid grid-cols-3 gap-2">
                  {trip.cargo.photos.map((photo, idx) =>
                <div
                  key={idx}
                  className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-80">
                  
                      <img src={photo.url} alt={photo.checkpoint} className="w-full h-full object-cover" />
                    </div>
                )}
                </div> :

              <div className="text-center py-6">
                  <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">{t("noPhotosUploaded", "No photos uploaded")}</p>
                  <p className="text-xs text-gray-400 mt-1">{t("photosWillAppear", "Photos will appear here when driver uploads them")}</p>
                </div>
              }
            </CardContent>
          </Card>
        </div>
      </div>
    </div>);

};

export default TripDetailPage;